import React, { useMemo, useState } from 'react';
import './LevelFormModal.css';

const LETTERS = 'abcdefghijklmnopqrstuvwxyz';

const disciplineOf = l => l.discipline || 'TRA';

const ladderOf = (levels, discipline = 'TRA') => levels
  .filter(l => l.type === 'SEQUENTIAL' && disciplineOf(l) === discipline)
  .sort((a, b) => a.number - b.number);

// The code for a new level at the end of the ladder: one more than the highest.
// Double mini levels are D1, D2...
export function nextLadderCode(levels, discipline = 'TRA') {
  const ladder = ladderOf(levels, discipline);
  const n = ladder.length ? ladder[ladder.length - 1].number + 1 : 1;
  return discipline === 'DMT' ? `D${n}` : String(n);
}

// The code for a new side path off a level: its code plus the next free letter (3 -> 3d)
export function nextSidePathCode(levels, base) {
  if (!base) return '';
  const taken = new Set(levels.map(l => String(l.identifier).toLowerCase()));
  const letter = [...LETTERS].find(c => !taken.has(`${base.identifier}${c}`.toLowerCase()));
  return `${base.identifier}${letter || ''}`;
}

// Add or edit a level. Adding asks where the level sits (next in the ladder,
// or a side path off an existing level) and works out its code from that.
export default function LevelFormModal({ level, levels, discipline = 'TRA', onSave, onCancel }) {
  const editing = Boolean(level);
  const ladder = useMemo(() => ladderOf(levels, discipline), [levels, discipline]);
  const last = ladder[ladder.length - 1];

  const [kind, setKind] = useState(level?.type || 'SEQUENTIAL');
  const [baseId, setBaseId] = useState(ladder[0]?.id || '');
  const [customCode, setCustomCode] = useState(null); // null = use the worked-out code
  const [name, setName] = useState(level?.name || '');
  const [description, setDescription] = useState(level?.description || '');
  const isDmt = (level?.discipline || discipline) === 'DMT';
  const [minPassTariff, setMinPassTariff] = useState(level?.minPassTariff != null ? String(Number(level.minPassTariff)) : '');
  const [saving, setSaving] = useState(false);

  const base = ladder.find(l => l.id === baseId);
  const suggested = kind === 'SEQUENTIAL' ? nextLadderCode(levels, discipline) : nextSidePathCode(levels, base);
  const code = customCode ?? suggested;
  const codeTaken = !editing && levels.some(l => String(l.identifier).toLowerCase() === code.trim().toLowerCase());

  const submit = async e => {
    e.preventDefault();
    setSaving(true);
    try {
      const tariff = isDmt ? { minPassTariff: minPassTariff === '' ? null : Number(minPassTariff) } : {};
      await onSave(editing
        ? { name, description, ...tariff }
        : { identifier: code.trim(), name, description, type: kind, discipline, ...tariff });
    } finally {
      setSaving(false);
    }
  };

  const placeText = editing
    ? (level.type === 'SEQUENTIAL'
      ? `Level ${level.identifier} in the ladder. Gymnasts work through the ladder in order.`
      : `Level ${level.identifier} is a side path, worked on alongside the ladder.`)
    : null;

  return (
    <div className="modal-overlay">
      <div className="modal level-form" role="dialog" aria-labelledby="level-form-title">
        <div className="modal-header">
          <h3 id="level-form-title">{editing ? `Edit Level ${level.identifier}` : discipline === 'DMT' ? 'Add a double mini level' : 'Add a level'}</h3>
          <button type="button" onClick={onCancel} className="close-button" aria-label="Close">×</button>
        </div>
        <form onSubmit={submit}>
          {editing ? (
            <p className="level-form__place">{placeText}</p>
          ) : (
            <fieldset className="level-form__section">
              <legend>Where does it go?</legend>
              <div className="level-form__kinds">
                <label className={`level-form__kind${kind === 'SEQUENTIAL' ? ' level-form__kind--on' : ''}`}>
                  <input type="radio" name="kind" checked={kind === 'SEQUENTIAL'} onChange={() => { setKind('SEQUENTIAL'); setCustomCode(null); }} />
                  <span className="level-form__kind-title">Next in the ladder</span>
                  <span className="level-form__kind-help">
                    {last ? `Comes after Level ${last.identifier}. Gymnasts finish Level ${last.identifier} first.` : 'The first level gymnasts work on.'}
                  </span>
                </label>
                <label className={`level-form__kind${kind === 'SIDE_PATH' ? ' level-form__kind--on' : ''}`}>
                  <input type="radio" name="kind" checked={kind === 'SIDE_PATH'} onChange={() => { setKind('SIDE_PATH'); setCustomCode(null); }} disabled={ladder.length === 0} />
                  <span className="level-form__kind-title">Side path</span>
                  <span className="level-form__kind-help">Extra skills worked on alongside a ladder level. Doesn't hold anyone back.</span>
                </label>
              </div>

              {kind === 'SIDE_PATH' && (
                <label className="level-form__field">
                  <span>Branches off</span>
                  <select className="form-control" value={baseId} onChange={e => { setBaseId(e.target.value); setCustomCode(null); }}>
                    {ladder.map(l => <option key={l.id} value={l.id}>Level {l.identifier}: {l.name}</option>)}
                  </select>
                </label>
              )}

              <div className="level-form__code">
                {customCode === null ? (
                  <>
                    <span>This will be <strong>Level {suggested}</strong>.</span>
                    <button type="button" className="level-form__link" onClick={() => setCustomCode(suggested)}>Use a different code</button>
                  </>
                ) : (
                  <label className="level-form__field level-form__field--inline">
                    <span>Code</span>
                    <input className="form-control" value={customCode} onChange={e => setCustomCode(e.target.value)} maxLength={20} required aria-label="Level code" />
                    <button type="button" className="level-form__link" onClick={() => setCustomCode(null)}>Use Level {suggested}</button>
                  </label>
                )}
                {codeTaken && <p className="level-form__error">There's already a Level {code.trim()}.</p>}
              </div>
            </fieldset>
          )}

          <label className="level-form__field">
            <span>Name</span>
            <input className="form-control" value={name} onChange={e => setName(e.target.value)} required placeholder="e.g. Advanced Landings" />
          </label>
          <label className="level-form__field">
            <span>Description <em>(optional)</em></span>
            <textarea className="form-control" value={description} onChange={e => setDescription(e.target.value)} rows={2} placeholder="What this level covers" />
          </label>
          {isDmt && (
            <label className="level-form__field">
              <span>Every pass at least <em>(optional)</em></span>
              <input className="form-control" type="number" min="0" max="99.9" step="0.1" value={minPassTariff} onChange={e => setMinPassTariff(e.target.value)} placeholder="e.g. 6.0" />
              <small className="text-muted">For a difficulty level. The level's passes become examples, and any two passes at this tariff count, including the gymnast's own checked passes.</small>
            </label>
          )}


          <div className="modal-actions">
            <button type="button" onClick={onCancel} className="btn btn-secondary" disabled={saving}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving || codeTaken || !code.trim()}>
              {saving ? 'Saving…' : editing ? 'Save changes' : `Add Level ${code.trim()}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
